import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

const kioskRequestConfig = {
  skipAuthRefresh: true,
};

// Auto-refresh on 401
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config || {};
    if (original.skipAuthRefresh) {
      return Promise.reject(error);
    }
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        await axios.post(`${API_URL}/auth/refresh`, {}, { withCredentials: true });
        return api(original);
      } catch {
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ===== AUTH =====
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  mfaChallenge: (data) => api.post('/auth/mfa/challenge', data),
  mfaVerify: (data) => api.post('/auth/mfa/verify', data),
  logout: () => api.post('/auth/logout', {}),
  changePassword: (data) => api.put('/auth/change-password', data),
  refresh: () => api.post('/auth/refresh', {}),
};

// ===== USERS =====
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  resetPassword: (id) => api.post(`/users/${id}/reset-password`),
  regenerateTimeClockPin: (id) => api.post(`/users/${id}/regenerate-timeclock-pin`),
  delete: (id) => api.delete(`/users/${id}`),
};

// ===== CENTERS =====
export const centersAPI = {
  getAll: () => api.get('/centers'),
  create: (data) => api.post('/centers', data),
  update: (id, data) => api.put(`/centers/${id}`, data),
  delete: (id) => api.delete(`/centers/${id}`),
};

// ===== SHIFTS =====
export const shiftsAPI = {
  getAll: (params) => api.get('/shifts', { params }),
  create: (data) => api.post('/shifts', data),
  update: (id, data) => api.put(`/shifts/${id}`, data),
  delete: (id) => api.delete(`/shifts/${id}`),
};

export const monthlyProgramsAPI = {
  get: (params) => api.get('/monthly-programs', { params }),
  save: (data) => api.put('/monthly-programs', data),
};

// ===== LEAVES =====
export const leavesAPI = {
  getAll: (params) => api.get('/leaves', { params }),
  create: (data) => api.post('/leaves', data),
  review: (id, data) => api.put(`/leaves/${id}/review`, data),
};

// ===== TIME CLOCK =====
export const timeClockAPI = {
  getAll: (params) => api.get('/timeclock', { params }),
  getStatus: () => api.get('/timeclock/status'),
  clockIn: (data) => api.post('/timeclock/clock-in', data),
  clockOut: () => api.put('/timeclock/clock-out'),
  kioskLogin: (data) => api.post('/timeclock/kiosk/login', data, kioskRequestConfig),
  kioskSearchStaff: (params, token) =>
    api.get('/timeclock/kiosk/staff', {
      ...kioskRequestConfig,
      params: {
        ...params,
        _ts: Date.now(),
      },
      headers: {
        Authorization: `Bearer ${token}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      },
    }),
  kioskClock: (data, token) =>
    api.post('/timeclock/kiosk/clock', data, {
      ...kioskRequestConfig,
      headers: { Authorization: `Bearer ${token}` },
    }),
};

// ===== BADGES =====
export const badgesAPI = {
  kioskLogin: (data) => api.post('/badges/kiosk/login', data, kioskRequestConfig),
  getAll: (params) => api.get('/badges', { params }),
  getById: (id) => api.get(`/badges/${id}`),
  create: (data) => api.post('/badges', data),
  bulkRegister: (badges) => api.post('/badges/bulk-register', { badges }),
  update: (id, data) => api.put(`/badges/${id}`, data),
  assign: (id, data) => api.put(`/badges/${id}/assign`, data),
  revoke: (id, data) => api.put(`/badges/${id}/revoke`, data),
  deactivate: (id) => api.put(`/badges/${id}/deactivate`),
  activate: (id) => api.put(`/badges/${id}/activate`),
  remove: (id) => api.delete(`/badges/${id}`),
  getScans: (params) => api.get('/badges/scans', { params }),
  updateCenterConfig: (centerId, data) => api.put(`/badges/centers/${centerId}/config`, data),
  scanKiosk: (data, kioskToken) =>
    api.post('/badges/scan', data, {
      ...kioskRequestConfig,
      headers: {
        Authorization: `Bearer ${kioskToken}`,
      },
    }),
  scanExternal: (data, apiKey) =>
    api.post('/badges/scan', data, {
      ...kioskRequestConfig,
      headers: {
        'x-badge-api-key': apiKey,
      },
    }),
};

// ===== PATIENTS =====
export const patientsAPI = {
  getAll: (params) => api.get('/patients', { params }),
  getById: (id) => api.get(`/patients/${id}`),
  updateRecord: (id, data) => api.put(`/patients/${id}/record`, data),
};

// ===== ASSIGNMENTS (PATIENT <-> STAFF) =====
export const assignmentsAPI = {
  getPatientAssignments: (patientId, params) => api.get(`/patients/${patientId}/assignments`, { params }),
  createPatientAssignment: (patientId, data) => api.post(`/patients/${patientId}/assignments`, data),
  endPatientAssignment: (patientId, assignmentId) => api.put(`/patients/${patientId}/assignments/${assignmentId}/end`),
  getStaffPatients: (staffId, params) => api.get(`/staff/${staffId}/patients`, { params }),
};

// ===== ACTIVITIES =====
export const activitiesAPI = {
  getAll: (params) => api.get('/activities', { params }),
  getTemplates: () => api.get('/activities/templates'),
  createTemplate: (data) => api.post('/activities/templates', data),
  updateTemplate: (id, data) => api.put(`/activities/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/activities/templates/${id}`),
  create: (data) => api.post('/activities', data),
  update: (id, data) => api.put(`/activities/${id}`, data),
  assignStaff: (activityId, data) => api.post(`/activities/${activityId}/assign-staff`, data),
  getEnrollments: (activityId) => api.get(`/activities/${activityId}/enrollments`),
  enroll: (activityId, data) => api.post(`/activities/${activityId}/enroll`, data),
  updateEnrollmentStatus: (activityId, enrollmentId, data) =>
    api.put(`/activities/${activityId}/enrollments/${enrollmentId}/status`, data),
};

// ===== REPORTS =====
export const reportsAPI = {
  getTimesheets: (params) => api.get('/reports/timesheets', { params }),
  getSummary: (params) => api.get('/reports/summary', { params }),
  getPresentNow: () => api.get('/reports/present-now'),
  getOperations: (params) => api.get('/reports/operations', { params }),
  getSafety: (params) => api.get('/reports/safety', { params }),
  getKpis: (params) => api.get('/reports/kpis', { params }),
  getPatientKpis: (params) => api.get('/reports/kpis/patients', { params }),
};

// ===== KPI DATA CAPTURE =====
export const kpiAPI = {
  getCareFollowups: (params) => api.get('/kpi/care-followups', { params }),
  createAssessment: (data) => api.post('/kpi/assessments', data),
  createSurvey: (data) => api.post('/kpi/surveys', data),
  createHospitalization: (data) => api.post('/kpi/hospitalizations', data),
  createCareFollowup: (data) => api.post('/kpi/care-followups', data),
  completeCareFollowup: (id) => api.put(`/kpi/care-followups/${id}/complete`),
};

// ===== TRANSPORT =====
export const transportAPI = {
  getBookings: (params) => api.get('/transport/bookings', { params }),
  getPatientTransport: (patientId, params) => api.get(`/patients/${patientId}/transport`, { params }),
  createBooking: (data) => api.post('/transport/bookings', data),
  updateBooking: (id, data) => api.put(`/transport/bookings/${id}`, data),
  updateBookingStatus: (id, data) => api.put(`/transport/bookings/${id}/status`, data),
};

// ===== INCIDENTS =====
export const incidentsAPI = {
  getAll: (params) => api.get('/incidents', { params }),
  getPatientIncidents: (patientId) => api.get(`/patients/${patientId}/incidents`),
  create: (data) => api.post('/incidents', data),
  update: (id, data) => api.put(`/incidents/${id}`, data),
  updateStatus: (id, data) => api.put(`/incidents/${id}/status`, data),
};

// ===== PERMISSIONS =====
export const permissionsAPI = {
  getMatrix: () => api.get('/permissions/matrix'),
  updateMatrix: (items) => api.put('/permissions/matrix', { items }),
};

export const privacyIncidentsAPI = {
  getAll: (params) => api.get('/privacy-incidents', { params }),
  create: (data) => api.post('/privacy-incidents', data),
  update: (id, data) => api.put(`/privacy-incidents/${id}`, data),
  updateStatus: (id, data) => api.put(`/privacy-incidents/${id}/status`, data),
};

export default api;
